/**
 * Voice Memo Prototype Utilities
 * Grouping and utility functions for the prototype
 */

import type { VoiceMemo, VoiceMemoPackage, VoiceMemoGroup } from './types';

/**
 * Generate packages from memos (group by date, with dynamic themes)
 */
export function generatePackages(memos: VoiceMemo[]): VoiceMemoPackage[] {
  const packagesByDate = new Map<string, VoiceMemo[]>();

  // Group memos by date
  memos.forEach(memo => {
    const date = memo.date;
    if (!packagesByDate.has(date)) {
      packagesByDate.set(date, []);
    }
    packagesByDate.get(date)!.push(memo);
  });

  // Create packages with theme metadata
  const packages: VoiceMemoPackage[] = [];
  packagesByDate.forEach((items, date) => {
    // Group items by theme dynamically
    const themes: { [themeName: string]: string[] } = {};
    items.forEach(item => {
      const theme = item.theme || 'uncategorized';
      if (!themes[theme]) {
        themes[theme] = [];
      }
      themes[theme].push(item.id);
    });

    // Generate unified analysis (mock)
    const allSummaries = items.filter(i => i.summary).map(i => i.summary!);
    const allActionItems = items.flatMap(i => i.actionItems || []);

    const packageId = `pkg-${date}`;
    const packageName = `${date} Voice Memos`;

    packages.push({
      id: packageId,
      packageName,
      packageDate: date,
      groupingType: 'daily',
      status: items.every(i => i.status === 'analyzed') ? 'analyzed' :
              items.some(i => i.status === 'transcribed' || i.status === 'analyzed') ? 'transcribed' : 'transcribed',
      packageMetadata: {
        themes,
        unifiedAnalysis: {
          summary: allSummaries.length > 0
            ? `Unified summary across ${items.length} memos: ${allSummaries.slice(0, 2).join(' ')}...`
            : undefined,
          actionItems: Array.from(new Set(allActionItems)).slice(0, 5),
          themes: Object.keys(themes)
        },
        groupingReason: 'Daily session'
      },
      items,
      createdAt: date
    });
  });

  return packages.sort((a, b) => b.packageDate.localeCompare(a.packageDate));
}

/**
 * Group memos by time proximity (within 1 hour)
 * Prototype version - uses prototype VoiceMemo type
 */
export function groupMemosBySession(memos: VoiceMemo[]): (VoiceMemo | VoiceMemoGroup)[] {
  const grouped: Map<string, VoiceMemo[]> = new Map();
  const ungrouped: VoiceMemo[] = [];

  // Group memos by date first
  const byDate = new Map<string, VoiceMemo[]>();
  memos.forEach(memo => {
    if (!byDate.has(memo.date)) {
      byDate.set(memo.date, []);
    }
    byDate.get(memo.date)!.push(memo);
  });

  // Within each date, group by time proximity
  byDate.forEach((dateMemos, date) => {
    // Sort by timestamp
    const sorted = dateMemos.sort((a, b) => {
      const timeA = a.timestamp || '000000';
      const timeB = b.timestamp || '000000';
      return timeA.localeCompare(timeB);
    });

    let currentSession: VoiceMemo[] = [];
    let sessionStartTime: string | null = null;

    sorted.forEach((memo) => {
      const memoTime = memo.timestamp || '000000';

      if (!sessionStartTime) {
        // Start new session
        sessionStartTime = memoTime;
        currentSession = [memo];
      } else {
        // Check if within 1 hour
        const startTime = parseInt(sessionStartTime.substring(0, 2)) * 3600 +
                         parseInt(sessionStartTime.substring(2, 4)) * 60 +
                         parseInt(sessionStartTime.substring(4, 6) || '0');
        const currentTime = parseInt(memoTime.substring(0, 2)) * 3600 +
                           parseInt(memoTime.substring(2, 4)) * 60 +
                           parseInt(memoTime.substring(4, 6) || '0');
        const diffSeconds = currentTime - startTime;

        if (diffSeconds <= 3600 && diffSeconds >= 0) {
          // Within 1 hour, add to current session
          currentSession.push(memo);
        } else {
          // Save current session and start new one
          if (currentSession.length > 1) {
            const sessionId = `session-${date}-${sessionStartTime}`;
            grouped.set(sessionId, currentSession);
          } else {
            ungrouped.push(...currentSession);
          }
          sessionStartTime = memoTime;
          currentSession = [memo];
        }
      }
    });

    // Save final session
    if (currentSession.length > 1) {
      const sessionId = `session-${date}-${sessionStartTime}`;
      grouped.set(sessionId, currentSession);
    } else if (currentSession.length === 1) {
      ungrouped.push(...currentSession);
    }
  });

  // Convert groups to array format
  const result: (VoiceMemo | VoiceMemoGroup)[] = [];

  // Add grouped sessions
  grouped.forEach((memos, sessionId) => {
    const firstMemo = memos[0];
    const sessionGroup: VoiceMemoGroup = {
      id: sessionId,
      date: firstMemo.date,
      startTime: firstMemo.timestamp || '000000',
      endTime: memos[memos.length - 1].timestamp || firstMemo.timestamp || '000000',
      memos
    };
    result.push(sessionGroup);
  });

  // Add ungrouped memos
  result.push(...ungrouped);

  // Sort by date (most recent first), then by time
  result.sort((a, b) => {
    const dateA = a instanceof Object && 'date' in a ? a.date : (a as VoiceMemo).date;
    const dateB = b instanceof Object && 'date' in b ? b.date : (b as VoiceMemo).date;
    if (dateA !== dateB) {
      return dateB.localeCompare(dateA);
    }
    const timeA = a instanceof Object && 'startTime' in a ? a.startTime : (a as VoiceMemo).timestamp || '000000';
    const timeB = b instanceof Object && 'startTime' in b ? b.startTime : (b as VoiceMemo).timestamp || '000000';
    return timeB.localeCompare(timeA);
  });

  return result;
}
